import React, { useCallback, useMemo } from "react";
import QATable, { ColumnDefinition } from "../../components/Design/Table/QATable";
import { ControllerAPI, ExecutionRO, RoleRO, WorkerRO } from "../../api/api";
import { stateToString } from "../Execution/ExecutionRoleTable";
import { DateTime } from "luxon";
import { makeStyles } from "@mui/styles";
import ColorDotLegend from "../../components/Data/ColorDotLegend";
import QATableButtonRow from "../../components/Design/Table/Layout/QATableButtonRow";
import QATableButton from "../../components/Design/Table/Controls/QATableButton";
import { ScreenshotMonitor, Subject } from "@mui/icons-material";
import { hasFailed } from "@miragon/react-utils-api";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import useDialog from "../../components/Hooks/Dialog/useDialog";
import WorkerScreenshotDialog from "./WorkerScreenshotDialog";

interface Props {
    roles: RoleRO[];
    workers: WorkerRO[];
    executions: ExecutionRO[];
}

const useStyles = makeStyles(() => ({
    category: {
        height: "100%",
        width: "calc(100% + 16px)",
        display: "flex",
        alignItems: "center",
        fontWeight: 500,
        padding: "0 1rem",
        marginLeft: "-16px"
    }
}));

const WorkerTable: React.FC<Props> = props => {
    const dialog = useDialog();
    const classes = useStyles();
    const snackbar = useSnackbar();

    const { workers, executions, roles } = props;

    const onOpenLogs = useCallback(async (workerId: string) => {
        const response = await ControllerAPI.getWorkerLogsUrl({ workerId });
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            window.open(response.result, "_blank");
        }
    }, [snackbar]);

    const onCaptureScreenshot = useCallback(async (workerId: string) => {
        dialog.open(WorkerScreenshotDialog, {
            workerId
        });
    }, [dialog]);

    const columns: ColumnDefinition<WorkerRO>[] = useMemo(() => [
        {
            id: "state",
            title: "Status",
            width: "150px",
            padding: {
                left: 16,
                top: 0,
                bottom: 0,
                right: 0
            },
            sortable: true,
            getValue: item => stateToString(item.workerState),
            renderCell: item => (
                <div
                    className={classes.category}
                    style={{
                        color: item.workerState === "WORKING" ? "white" : "black",
                        backgroundColor: item.workerState === "WORKING" ? "green" : "lightgrey"
                    }}>
                    {stateToString(item.workerState)}
                </div>
            )
        },
        {
            id: "id",
            title: "ID",
            width: "auto",
            sortable: true,
            getValue: item => item.id
        },
        {
            id: "execution",
            title: "Ausführung",
            width: "250px",
            sortable: true,
            getValue: item => item.workerAssignment ? (executions.find(e => e.id === item.workerAssignment?.executionId)?.name ?? "Unbekannt") : "Keine"
        },
        {
            id: "role",
            title: "Rolle",
            width: "250px",
            sortable: true,
            getValue: item => item.workerAssignment ? (roles.find(r => r.id === item.workerAssignment?.roleId)?.name ?? "Unbekannt") : "Keine"
        },
        {
            id: "ping",
            title: "Letzter Ping",
            width: "180px",
            sortable: true,
            getValue: item => DateTime.fromISO(item.lastHeartbeat).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
        },
        {
            id: "actions",
            width: "70px",
            padding: 0,
            getValue: () => undefined,
            renderCell: item => (
                <QATableButtonRow>
                    <QATableButton
                        icon={Subject}
                        onClicked={() => onOpenLogs(item.id)} />
                    <QATableButton
                        icon={ScreenshotMonitor}
                        onClicked={() => onCaptureScreenshot(item.id)} />
                </QATableButtonRow>
            )
        }
    ], [classes.category, executions, onCaptureScreenshot, onOpenLogs, roles]);

    return (
        <>
            <QATable
                defaultSortByColumn="name"
                items={workers}
                defaultPageSize={25}
                getId={item => item.id}
                fallbackText="Keine Worker gefunden."
                columns={columns} />
            <ColorDotLegend items={[
                { color: "lightgrey", name: `${workers.filter(e => e.workerState === "IDLE").length} Bereit` },
                { color: "green", name: `${workers.filter(e => e.workerState === "WORKING").length} Arbeitet` }
            ]} />
        </>
    );
};

export default WorkerTable;
